import React, { FC, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dropdown } from "flowbite-react";
import { HiBell, HiX } from "react-icons/hi";
import { ChatContext, ChatContextInterface } from "../components/MSRContext";
import Global from "../globals/globals";
import { formatLocalScheduleTime } from "../utils/calendarExport";

const Notifications: FC = function () {
    const { notificationController, setnotifCount } = useContext(ChatContext) as ChatContextInterface;
    const [notifications, setNotifications] = useState(notificationController.Notifications);
    const { t } = useTranslation("nav");

    useEffect(() => {
        const handleNotificationsUpdate = () => {
            console.log("Notifications updated, refreshing component");
            setNotifications([...notificationController.Notifications]);
            setnotifCount(notificationController.Notifications.length);
        };

        window.addEventListener("notificationsUpdate", handleNotificationsUpdate);
        return () => {
            window.removeEventListener("notificationsUpdate", handleNotificationsUpdate);
        };
    }, [notificationController, setnotifCount]);

    useEffect(() => {
        setNotifications([...notificationController.Notifications]);
        setnotifCount(notificationController.Notifications.length);
    }, [notificationController]);

    const handleRemoveNotification = (notificationId: string) => {
        notificationController.removeNotificationById(notificationId);
    };

    const isScheduleNotification = (notification: any) => notification?.event === "media-comm-sched" && !!notification?.detail;

    const isSelfScheduledNotification = (notification: any) => {
        if (!isScheduleNotification(notification)) return false;

        const fromName = String(notification?.detail?.fromName || "").toLowerCase();
        const currentUser = String(`${Global.username}${Global.companyDomain}` || "").toLowerCase();
        const fromLocal = fromName.split("@")[0];
        const currentLocal = currentUser.split("@")[0];

        return !!fromName && (fromName === currentUser || fromLocal === currentLocal);
    };

    const openCalendarChooser = (payload: { title: string; description: string; startTime: Date; endTime: Date }) => {
        window.dispatchEvent(new CustomEvent("open-calendar-export-chooser", { detail: payload }));
    };

    const handleAddToCalendar = (notification: any) => {
        const schedule = notification?.detail || {};
        const startSeconds = Number(schedule?.schedTime || 0);
        if (!startSeconds) {
            return;
        }

        const durationHours = Number(schedule?.duration || 0.5);
        const startTime = new Date(startSeconds * 1000);
        const endTime = new Date(startTime.getTime() + durationHours * 60 * 60 * 1000);
        const commType = schedule?.commType || "media";
        const fromName = schedule?.fromName || "Someone";

        openCalendarChooser({
            title: `${commType === "audio+video" ? "Video" : commType === "audio" ? "Audio" : "Media"} call with ${fromName.split("@")[0]}`,
            description: `Scheduled via HandyChat from ${fromName}`,
            startTime,
            endTime,
        });
    };

    const renderNotificationText = (notification: any) => {
        if (isScheduleNotification(notification)) {
            const schedule = notification.detail;
            const formattedTime = schedule?.schedTime ? formatLocalScheduleTime(Number(schedule.schedTime)) : "soon";
            return `${schedule?.fromName?.split("@")[0] || "Someone"} scheduled ${schedule?.commType || "media"} call for ${formattedTime}`;
        }

        if (typeof notification.detail === "string") return notification.detail;
        if (notification.detail?.body) return notification.detail.body;
        return "Notification";
    };

    // const formatTimeAgo = (createdTime: Date) => {
    //     if (!createdTime) return 'Recently';

    //     const now = new Date().getTime();
    //     const created = createdTime.getTime();
    //     const diffMinutes = Math.floor((now - created) / (1000 * 60));

    //     if (diffMinutes < 1) return 'Just now';
    //     if (diffMinutes < 60) return `${diffMinutes}m ago`;
    //     const diffHours = Math.floor(diffMinutes / 60);
    //     if (diffHours < 24) return `${diffHours}h ago`;
    //     const diffDays = Math.floor(diffHours / 24);
    //     return `${diffDays}d ago`;
    // };

    return (
        <Dropdown
            arrowIcon={false}
            inline
            label={
                <span className="relative p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200">
                    <span className="sr-only">{t('notificationsTitle')}</span>
                    <HiBell className="text-xl text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors duration-200" />
                    {notifications.length > 0 && (
                        <span className="absolute flex items-center justify-center min-w-[18px] h-[18px] text-xs font-medium text-white rounded-full bg-blue-500 -top-1 -right-1 animate-pulse">
                            {notifications.length}
                        </span>
                    )}
                </span>
            }
        >
            <div className="w-80 max-w-sm">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 text-sm font-semibold text-gray-700 bg-gray-50 rounded-t-lg dark:bg-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-600">
                    <span className="flex items-center gap-2">
                        <HiBell className="text-base" />
                        {t('notificationsTitle')}
                    </span>
                    {notifications.length > 0 && (
                        <span className="text-xs bg-gray-200 dark:bg-gray-600 px-2 py-1 rounded-full">
                            {notifications.length}
                        </span>
                    )}
                </div>
                <div className="max-h-96 overflow-y-auto">
                    {notifications.length > 0 ? (
                        notifications.map((notification, index) => (
                            <div
                                key={notification.id}
                                role="button"
                                tabIndex={0}
                                onClick={() => handleRemoveNotification(notification.id)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        handleRemoveNotification(notification.id);
                                    }
                                }}
                                className={`relative px-4 py-3 border-b border-gray-100 dark:border-gray-600 transition-colors duration-200 ${index === notifications.length - 1 ? 'border-b-0' : ''} cursor-pointer`}
                            >
                                <div className="flex items-start gap-3">
                                    {/* Notification Icon */}
                                    <div className="flex-shrink-0 mt-1">
                                        <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-full flex items-center justify-center">
                                            <NotificationIcon />
                                        </div>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm text-gray-900 dark:text-white leading-relaxed mb-2">
                                                    {renderNotificationText(notification)}
                                                </p>
                                                {isScheduleNotification(notification) && !isSelfScheduledNotification(notification) && (
                                                    <button
                                                        type="button"
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            handleAddToCalendar(notification);
                                                        }}
                                                        className="mb-2 inline-flex items-center rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 transition-colors duration-200"
                                                    >
                                                        Add to calendar
                                                    </button>
                                                )}
                                                {/* <p className="text-xs text-gray-500 dark:text-gray-400">
                                                    {formatTimeAgo(notification.timestamp)}
                                                </p> */}
                                            </div>
                                            <button
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    handleRemoveNotification(notification.id);
                                                }}
                                                className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded transition-colors duration-200"
                                                title={t('removeNotification')}
                                            >
                                                <HiX className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="px-4 py-8 text-center">
                            <div className="flex flex-col items-center gap-2">
                                <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
                                    <HiBell className="w-6 h-6 text-gray-400" />
                                </div>
                                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('noNotifications')}</p>
                                <p className="text-xs text-gray-400 dark:text-gray-500">{t('caughtUp')}</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Dropdown>
    );
};

const NotificationIcon: FC = function () {
    return (
        <svg
            className="w-4 h-4 text-white"
            fill="currentColor"
            viewBox="0 0 20 20"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                fillRule="evenodd"
                d="M18 13V5a2 2 0 00-2-2H4a2 2 0 00-2 2v8a2 2 0 002 2h3l3 3 3-3h3a2 2 0 002-2zM5 7a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1zm1 3a1 1 0 100 2h3a1 1 0 100-2H6z"
                clipRule="evenodd"
            />
        </svg>
    );
};

export default Notifications;